import { useQuery } from "@tanstack/react-query";
import { set } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { providerApi } from "../../../api/providerApi";
import { NotificationVariant } from "../../../components/dashboard/NotificationsProvider";
import useFullPageLoader from "../../../hooks/useFullPageLoader";
import useNotifications from "../../../hooks/useNotifications";
import { useSelector } from "../../../store";
import { Organization } from "../../../types/organization";
import { useOrgContactModal } from "./useOrgContactModal";
import useAuth from "../../../hooks/useAuth";

export const useOrganizationReference = () =>
  useSelector((state) => state.user.organizationReference);

/** Current organization from store (selected org). */
export const useCurrentOrganization = () =>
  useSelector((state) => state.user.organization);

export const useOrganization = () => {
  const organizationReference = useOrganizationReference();
  const currentOrganization = useCurrentOrganization();

  const { contactsList, setContactsList } = useOrgContactModal();
  const { organizations } = useAuth();
  const [, setLoading] = useFullPageLoader();
  const { setNotification } = useNotifications();
  const [organization, setOrganization] = useState<Organization>(null);
  const THIRTY_SECONDS_IN_MS = 30 * 1000;
  const updateOrganization = (field: string, value: any) => {
    const update = { ...organization };
    set(update, field, value);
    setOrganization(update);
  };

  const orgId = currentOrganization?.authId ?? currentOrganization?.id;
  const countryCode = useMemo(
    () =>
      organizations?.find(
        (org: Organization) => org.id === organizationReference?.identifier,
      )?.country,
    [organizations, organizationReference?.identifier],
  );
  const {
    data: organizationData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["organization", orgId],
    queryFn: () => providerApi.findOrganization(orgId),
    // eslint-disable-next-line @tanstack/query/no-deprecated-options
    onError: () => {
      setNotification({
        message: "An error occurred while fetching organization",
        variant: NotificationVariant.error,
      });
    },
    enabled: !!orgId,
    staleTime: THIRTY_SECONDS_IN_MS,
  });

  useEffect(() => {
    if (!orgId) return;
    setLoading(isLoading);
    if (organizationData) setOrganization(organizationData);
    if (error) console.error(`Error finding Org ${error}`);
  }, [organizationData, isLoading, error, setLoading, orgId]);

  // CONTACTS
  const generateContactId = (contactList) => {
    if (contactList) {
      contactList.map(
        (contact, index) =>
          (contact.id = `${contact.name[0].firstName}${index}`),
      );
    }
  };

  useEffect(() => {
    if (organization) {
      generateContactId(organization?.contact || []);
      setContactsList(organization?.contact || []);
    }
  }, [organization]);

  const updateContactList = (contact) => {
    const update = [...contactsList];
    const index = update.findIndex((c) => c.id === contact.id);
    if (index > -1) {
      update[index] = contact;
    } else {
      update.push({ ...contact, id: contact.name[0].firstName + index });
    }
    setContactsList(update);
  };

  const hasLinkedOrganizations = organization?.linkedOrganizations?.length > 0;
  return {
    setOrganization,
    organization,
    countryCode,
    contactsList,
    updateOrganization,
    updateContactList,
    organizationReference,
    hasLinkedOrganizations,
  };
};
